<script setup lang="ts">
/**
 * Compact colour picker. Combines:
 *  - a row of preset swatches (the most common picks),
 *  - a native `<input type="color">` for free-form selection,
 *  - a Clear button that emits empty string.
 *
 * Value is `#rrggbb` lowercase, or `''` for "no colour". Empty is
 * the meaningful default for our tag / accent-category fields —
 * Carbon Fields stores it that way and we round-trip it through.
 *
 * A live preview chip below the controls shows the picked colour with
 * a contrast-picked text colour, so editors can immediately tell if
 * the choice will be readable on the public site.
 */
import { computed } from 'vue'
import { Check, X } from '@lucide/vue'

import { cn } from '@/shared/lib/cn'
import { isHexColor, readableTextColor } from '@/shared/lib/color'

interface Props {
  modelValue: string
  disabled?: boolean
  /** Text rendered in the preview chip — usually the entity's name. */
  previewLabel?: string
  /** Override the preset row; defaults to a 9-swatch palette. */
  presets?: readonly string[]
  /** Show the Clear button (emits ''). Off for fields that must always
   *  hold a colour, e.g. QR foreground/background. */
  allowClear?: boolean
  /** Tighter swatches and no preview chip — for dense layouts where a
   *  live preview already exists elsewhere (e.g. the QR studio). */
  compact?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  previewLabel: 'Preview',
  allowClear: true,
  compact: false,
  presets: () =>
    [
      '#ef4444', // red
      '#f59e0b', // amber
      '#fde047', // yellow
      '#22c55e', // green
      '#14b8a6', // teal
      '#3b82f6', // blue
      '#a855f7', // violet
      '#ec4899', // pink
      '#475569', // slate
    ] as const,
})
const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const normalised = computed(() => props.modelValue.toLowerCase())
const hasColor = computed(() => isHexColor(normalised.value))
const previewText = computed(() =>
  hasColor.value ? readableTextColor(normalised.value) : '#000000',
)

function pick(hex: string) {
  if (props.disabled) return
  emit('update:modelValue', hex.toLowerCase())
}

function onNativeChange(e: Event) {
  const t = e.target as HTMLInputElement
  emit('update:modelValue', (t.value || '').toLowerCase())
}

function clear() {
  if (props.disabled) return
  emit('update:modelValue', '')
}
</script>

<template>
  <div :class="compact ? 'space-y-1.5' : 'space-y-2'">
    <div :class="cn('flex flex-wrap items-center', compact ? 'gap-1.5' : 'gap-2')">
      <button
        v-for="hex in presets"
        :key="hex"
        type="button"
        :class="
          cn(
            'inline-flex shrink-0 items-center justify-center rounded-full border border-border outline-offset-2 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            compact ? 'size-6' : 'size-7',
            normalised === hex && 'ring-2 ring-ring',
          )
        "
        :style="{ backgroundColor: hex }"
        :disabled="disabled"
        :aria-label="`Pick colour ${hex}`"
        :aria-pressed="normalised === hex"
        @click="pick(hex)"
      >
        <Check
          v-if="normalised === hex"
          :class="cn('size-3.5')"
          :style="{ color: readableTextColor(hex) }"
          aria-hidden="true"
        />
      </button>

      <!-- Native picker for anything outside the presets. Hidden visually,
           triggered by a labelled chip so the click target is uniform. -->
      <label
        :class="
          cn(
            'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full border border-dashed border-border text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted',
            compact ? 'size-6' : 'size-7',
            disabled && 'cursor-not-allowed opacity-50',
          )
        "
        :title="hasColor ? `Custom: ${normalised}` : 'Custom colour'"
      >
        <span aria-hidden="true">+</span>
        <span class="sr-only">Custom colour picker</span>
        <input
          type="color"
          class="sr-only"
          :value="hasColor ? normalised : '#888888'"
          :disabled="disabled"
          @input="onNativeChange"
        />
      </label>

      <button
        v-if="allowClear && hasColor"
        type="button"
        class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="disabled"
        @click="clear"
      >
        <X class="size-3.5" aria-hidden="true" />
        Clear
      </button>
    </div>

    <div
      v-if="!compact"
      :class="
        cn(
          'inline-flex max-w-full items-center gap-1.5 truncate rounded-md border border-border px-2 py-1 text-xs font-medium',
          !hasColor && 'bg-muted text-muted-foreground',
        )
      "
      :style="hasColor ? { backgroundColor: normalised, color: previewText } : undefined"
    >
      <span class="truncate">{{ previewLabel }}</span>
      <span v-if="hasColor" class="font-mono opacity-70">{{ normalised }}</span>
      <span v-else class="opacity-70">No colour</span>
    </div>
  </div>
</template>
